import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { JobSearchResult } from '../types/job';
import { AlgoliaJobStrategy } from '../services/search/strategies/AlgoliaSearchStrategy';
import { SupabaseJobStrategy } from '../services/search/strategies/SupabaseSearchStrategy';
import { JobSearchParams } from '../services/search/SearchStrategy';

type SearchMode = 'search' | 'spotlight';

interface JobSearchStoreState {
  results: JobSearchResult[];
  spotlightedResults: JobSearchResult[];
  suggestions: string[];
  loading: boolean;
  suggestionsLoading: boolean;
  error: string | null;
  spotlightedError: string | null;
  suggestionsError: string | null;
  hasMore: boolean;
  lastSearchParams: JobSearchParams | null;
  searchMode: SearchMode;
  search_jobs: (params: JobSearchParams) => Promise<void>;
  fetch_more_jobs: () => Promise<void>;
  fetch_spotlighted_jobs: () => Promise<void>;
  fetch_job_suggestions: (keyword: string) => Promise<void>;
  fetchJobById: (jobId: string) => Promise<JobSearchResult | null>;
}

const algoliaStrategy = new AlgoliaJobStrategy();
const supabaseStrategy = new SupabaseJobStrategy();

const getErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : 'Unknown error';

export const useJobSearchStore = create<JobSearchStoreState>((set, get) => {
  async function fetchAndSetResults(params: JobSearchParams, append = false) {
    const {
      keyword = '',
      filter_service_category_slugs = [],
      filter_city_ids = [],
      filter_languages = [],
      limit_count = 10,
      offset_count = 0,
      sort_order = null,
    } = params;

    // Reset to spotlight if no search criteria
    if (
      !keyword.trim() &&
      filter_service_category_slugs.length === 0 &&
      filter_city_ids.length === 0 &&
      filter_languages.length === 0 &&
      !sort_order
    ) {
      set({ searchMode: 'spotlight', results: [], loading: false, error: null });
      return;
    }

    set({ loading: true, error: null, searchMode: 'search' });

    try {
      const { results: rawResults, hasMore } = await supabaseStrategy.search({
        keyword,
        filter_service_category_slugs,
        filter_city_ids,
        filter_languages,
        limit_count,
        offset_count,
        sort_order,
      });

      set(state => ({
        results: append ? [...state.results, ...rawResults] : rawResults,
        loading: false,
        hasMore,
        lastSearchParams: { ...params, limit_count, offset_count },
      }));
    } catch (err: unknown) {
      console.error('Job Search error:', err);
      set({ error: getErrorMessage(err), loading: false, hasMore: false });
    }
  }

  return {
    results: [],
    spotlightedResults: [],
    suggestions: [],
    loading: false,
    suggestionsLoading: false,
    error: null,
    spotlightedError: null,
    suggestionsError: null,
    hasMore: false,
    lastSearchParams: null,
    searchMode: 'spotlight',

    search_jobs: params => fetchAndSetResults(params, false),

    fetch_more_jobs: async () => {
      const state = get();
      const params = state.lastSearchParams;
      if (!params) return Promise.resolve();
      return fetchAndSetResults({ ...params, offset_count: state.results.length }, true);
    },

    async fetch_spotlighted_jobs() {
      if (get().searchMode === 'search') return;
      set({ spotlightedError: null, searchMode: 'spotlight', loading: true });
      const { data, error } = await supabase.rpc('fetch_spotlighted_jobs');
      if (error) {
        set({ spotlightedError: error.message, loading: false });
      } else {
        set({ spotlightedResults: data || [], loading: false });
      }
    },

    async fetch_job_suggestions(keyword: string) {
      if (!keyword || keyword.length < 2) {
        set({ suggestions: [], suggestionsLoading: false });
        return;
      }
      set({ suggestionsLoading: true, suggestionsError: null });

      const isAlgoliaConfigured = !!(
        import.meta.env.VITE_ALGOLIA_APP_ID && import.meta.env.VITE_ALGOLIA_SEARCH_KEY
      );

      try {
        if (isAlgoliaConfigured && algoliaStrategy.fetchSuggestions) {
          const suggestions = await algoliaStrategy.fetchSuggestions(keyword);
          set({ suggestions, suggestionsLoading: false });
        } else {
          // Fallback to Supabase RPC
          const { data, error } = await supabase.rpc('search_job_keywords_suggestions', {
            p_keyword: keyword,
          });
          if (error) throw error;
          set({
            suggestions: Array.isArray(data)
              ? data
                  .map(item =>
                    typeof item === 'object' && item !== null && 'keyword' in item
                      ? item.keyword
                      : undefined
                  )
                  .filter((keyword): keyword is string => typeof keyword === 'string')
              : [],
            suggestionsLoading: false,
          });
        }
      } catch (err: unknown) {
        set({ suggestionsError: getErrorMessage(err), suggestionsLoading: false });
      }
    },

    async fetchJobById(jobId: string) {
      const { data, error } = await supabase.rpc('search_jobs', {
        keyword: null,
        filter_languages: null,
        filter_city_ids: null,
        filter_service_category_slugs: null,
        filter_listing_type_ids: null,
        filter_on_site: null,
        sort_order: null,
        limit_count: 1,
        offset_count: 0,
        p_job_id: jobId,
      });

      if (error) {
        console.error('Error fetching job by id:', error);
        return null;
      }

      return data?.[0] ?? null;
    },
  };
});
